import React, { useMemo } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { useBasesComerciais } from '../hooks';
import { formatVigencia } from '../utils/format';

interface CommercialBaseOption {
  id: string;
  codigo: string;
  versao: number;
  nome: string;
  moeda: string;
  dataInicio: string;
  dataFim: string | null;
  situacao: string;
  valorTotal: string;
}

interface Props {
  /** Cliente cujas Bases vigentes serão listadas. */
  clientId: string;
  value: string;
  onChange: (value: string) => void;
  /** Opção "nenhuma Base aplicada". */
  allowNone?: boolean;
  disabled?: boolean;
  label?: string;
  error?: string;
}

/**
 * Seletor de Base Comercial reutilizável (task 12.1).
 *
 * Lista apenas as Bases **vigentes** do cliente informado — a Base
 * expirada aparece como indisponível (RN-COM-023/030). Exibe
 * código/versão/vigência. Consumido pelo modal de geração de proposta
 * e, futuramente, por Contratos e OS.
 */
export const CommercialBaseSelect: React.FC<Props> = ({
  clientId,
  value,
  onChange,
  allowNone = true,
  disabled,
  label = 'Base Comercial',
  error,
}) => {
  const { data, isLoading } = useBasesComerciais({
    clientId,
    limit: 100,
  });

  const options = useMemo(() => {
    const bases = (data?.data ?? []).filter((b) => b.situacao === 'vigente');
    const opts: { value: string; label: string; description?: string; disabled?: boolean }[] = [];
    if (allowNone) {
      opts.push({ value: '', label: 'Nenhuma Base aplicada' });
    }
    for (const b of bases) {
      opts.push({
        value: b.id,
        label: `${b.codigo} v${b.versao} — ${b.nome}`,
        description: `${formatVigencia(b.dataInicio, b.dataFim)} • ${b.moeda}`,
      });
    }
    return opts;
  }, [data, allowNone]);

  return (
    <AutocompleteSelect
      label={isLoading ? 'Carregando Bases…' : label}
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled || isLoading}
      error={error}
    />
  );
};

export type { CommercialBaseOption };
